export type UserActionTrigger = "addToCart" | "removeFromCart" | "placeOrder" | "completeRegistration" | "loginSuccess";
export type PostAuthUserActionTrigger = Extract<UserActionTrigger, "completeRegistration" | "loginSuccess">;

export type UserActionTriggerIntent = {
  trigger: PostAuthUserActionTrigger;
  targetPath: string;
  createdAt: number;
};

export const USER_ACTION_TRIGGER_INTENT_TTL_MS = 60 * 1000;

const STORAGE_KEY = "jti.userActionTriggerIntent";

const isBrowser = () => typeof window !== "undefined" && typeof window.sessionStorage !== "undefined";

function normalizePath(path: string): string {
  const normalizedPath = path.endsWith("/") && path.length > 1 ? path.slice(0, -1) : path;
  return normalizedPath || "/";
}

function getCurrentUrlBase(): string {
  return /^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(window.location.href)
    ? window.location.href
    : window.location.origin || "http://localhost";
}

function getPathFromUrl(url: string): string {
  return normalizePath(new URL(url, getCurrentUrlBase()).pathname);
}

function readIntent(): UserActionTriggerIntent | undefined {
  if (!isBrowser()) return undefined;

  const rawIntent = window.sessionStorage.getItem(STORAGE_KEY);
  if (!rawIntent) return undefined;

  try {
    const parsedIntent = JSON.parse(rawIntent) as Partial<UserActionTriggerIntent>;

    if (
      (parsedIntent.trigger !== "completeRegistration" && parsedIntent.trigger !== "loginSuccess") ||
      typeof parsedIntent.targetPath !== "string" ||
      typeof parsedIntent.createdAt !== "number"
    ) {
      window.sessionStorage.removeItem(STORAGE_KEY);
      return undefined;
    }

    return {
      trigger: parsedIntent.trigger,
      targetPath: normalizePath(parsedIntent.targetPath),
      createdAt: parsedIntent.createdAt,
    };
  } catch (error) {
    console.warn("[userActionTriggerIntent] Failed to parse stored intent", error);
    window.sessionStorage.removeItem(STORAGE_KEY);
    return undefined;
  }
}

export function persistUserActionTriggerIntent(trigger: PostAuthUserActionTrigger, targetUrl: string): void {
  if (!isBrowser()) return;

  try {
    const intent: UserActionTriggerIntent = {
      trigger,
      targetPath: getPathFromUrl(targetUrl),
      createdAt: Date.now(),
    };

    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(intent));
  } catch (error) {
    console.warn("[userActionTriggerIntent] Failed to persist intent", error);
  }
}

export function consumeUserActionTriggerIntent(
  expectedTrigger: PostAuthUserActionTrigger,
  currentUrl = isBrowser() ? window.location.href : ""
): UserActionTriggerIntent | undefined {
  if (!isBrowser()) return undefined;

  const intent = readIntent();
  if (!intent) return undefined;

  if (Date.now() - intent.createdAt > USER_ACTION_TRIGGER_INTENT_TTL_MS) {
    window.sessionStorage.removeItem(STORAGE_KEY);
    return undefined;
  }

  if (getPathFromUrl(currentUrl) !== intent.targetPath) {
    window.sessionStorage.removeItem(STORAGE_KEY);
    return undefined;
  }

  if (intent.trigger !== expectedTrigger) {
    return undefined;
  }

  window.sessionStorage.removeItem(STORAGE_KEY);

  return intent;
}

export function clearUserActionTriggerIntent(): void {
  if (!isBrowser()) return;

  window.sessionStorage.removeItem(STORAGE_KEY);
}
